import { useCallback } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { useIxmUserauthV1AccessRequestCommandServiceCreateAccessRequest } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-access-request-command-service-create-access-request';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box, DEFAULT_BOX_PADDING_NO_HORIZONTAL } from '@interxfi/design-system/components/box/box';
import { Button } from '@interxfi/design-system/components/button/button';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { usPhoneMask } from '@interxfi/design-system/components/input/text-input/masks';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography, type TypographyProps } from '@interxfi/design-system/components/typography';
import { AppTextInputWithLens } from '../../components/form/app-text-input';
import { AuthLayout } from '../../features/auth/layout';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../util/error-handler';
import { DEFAULT_UNAUTHENTICATED_ROUTE } from '../../util/routing/defaults';
import { getPageHead } from '../../util/routing/meta';

export const Route = createFileRoute('/_auth/request-access')({
  head: () => getPageHead('Request Access'),
  component: RequestAccess,
});

const typographyProps = {
  lineHeight: '1.125rem',
  size: '0.875rem',
} as const satisfies TypographyProps;

const requestAccessFormSchema = z.object({
  firstName: z.string().nonempty({ error: 'First Name is required' }),
  lastName: z.string().nonempty({ error: 'Last Name is required' }),
  jobTitle: z.string().nonempty({ error: 'Job Title is required' }),
  companyName: z.string().nonempty({ error: 'Company Name is required' }),
  email: z.email({ error: 'Work Email must be a valid email' }).nonempty({ error: 'Work Email is required' }),
  phone: z.string(),
});

type RequestAccessFormValues = z.infer<typeof requestAccessFormSchema>;

const errorHandler = buildDefaultErrorMessageGetter('An error occurred while submitting your access request. Please try again.');

const defaultValues = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  companyName: '',
  email: '',
  phone: '',
} as const satisfies RequestAccessFormValues;

function RequestAccess() {
  const addToast = useAddToast();
  const { isPending, error, mutateAsync } = useIxmUserauthV1AccessRequestCommandServiceCreateAccessRequest();
  useErrorHandler(error, errorHandler);
  const [buttonState, buttonRef] = useButtonState(isPending, error);
  const form = useForm({
    defaultValues,
    resolver: zodResolver(requestAccessFormSchema),
    mode: 'all',
  });
  const {
    control,
    formState: { isValid },
  } = form;

  const lens = useLens({ control });

  const handleSubmit = useCallback(
    async (values: RequestAccessFormValues) => {
      try {
        const result = await mutateAsync({ spec: values });

        if (result) {
          addToast({
            id: 'requestAccessSuccess',
            variant: 'success',
            content: 'Your access request has been submitted, and will be reviewed by our team.',
          });

          form.reset();
        }
      } catch {}
    },
    [addToast, mutateAsync, form.reset],
  );

  return (
    <FormProvider {...form}>
      <AuthLayout withFilledBackgroundGraphic>
        <HorizontalStack align="space-between" blockAlign="center">
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
            Request Access
          </Typography>

          <TextLink LinkElement={Link} size="medium" to={DEFAULT_UNAUTHENTICATED_ROUTE} underline="hover">
            Sign In
          </TextLink>
        </HorizontalStack>

        <VerticalStack as="form" gap={4} onSubmit={form.handleSubmit(handleSubmit)}>
          <Grid autoColumns={2} gap={4}>
            <AppTextInputWithLens label="First Name" lens={lens.focus('firstName')} required />
            <AppTextInputWithLens label="Last Name" lens={lens.focus('lastName')} required />
          </Grid>

          <AppTextInputWithLens label="Job Title" lens={lens.focus('jobTitle')} required />
          <AppTextInputWithLens label="Company Name" lens={lens.focus('companyName')} required />
          <AppTextInputWithLens label="Work Email" lens={lens.focus('email')} required />
          <AppTextInputWithLens label="Mobile Phone" lens={lens.focus('phone')} maskOptions={usPhoneMask} />

          <Box padding={DEFAULT_BOX_PADDING_NO_HORIZONTAL}>
            <Typography {...typographyProps}>
              By proceeding, you agree to our{' '}
              <TextLink to="https://interchange.com/disclosures" typography={typographyProps} underline="always">
                Terms &amp; Conditions
              </TextLink>
              .
            </Typography>
          </Box>

          <Button disabled={!isValid} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
            Submit
          </Button>
        </VerticalStack>
      </AuthLayout>
    </FormProvider>
  );
}
